<template>
  <section class="container py-5">
    <div v-if="loading">Loading...</div>
    <div v-else>
      <p>
        <strong>E-mail:</strong>
        {{ user.email }}
        <span class="text-success" v-if="user.emailVerified">(verified)</span>
        <span v-else>(not verified)</span>
      </p>
      <p>
        <strong>SMS Multi-factor Authentication: </strong>
        {{ getPhoneNumber() }}
        <span class="text-success" v-if="isSMSFactorEnabled">(enabled)</span>
        <button class="btn btn-primary btn-sm ml-2" v-if="!isSMSFactorEnabled && !showSMSEnrollment" @click="toggleSMSEnrollment">Enable</button>
      </p>
      <p v-if="user.displayName">
        <strong>Name:</strong>
        {{ user.displayName }}
      </p>
      <div class="row">
        <SMSEnrollment class="col-md-6" v-if="showSMSEnrollment"></SMSEnrollment>
      </div>
      <div>
        <p>Email notifications for ASPSP status changes</p>
        <div v-if="brokerOrigins.length > 1" class="form-inline d-flex justify-content-center">
          <select v-model="brokerOrigin" name="brokerOrigin" id="brokerOrigin" class="form-control">
            <option v-for="origin in brokerOrigins" :key="origin.value" :value="origin.value">
              {{ origin.name }}
            </option>
          </select>
        </div>
        <div><p></p></div>
        <div v-if="Object.keys(subscriptions).length">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th class="align-middle">Country</th>
              <th class="align-middle">PSU Type</th>
              <th class="align-middle">Service</th>
              <th class="align-middle">
                Unsubscribe
                  <label class="form-check-label ml-1">
                    <input
                      type="checkbox"
                      @change="toggleUnsubscribeAll($event.target.checked)"
                      :checked="Object.keys(subscriptions).every(country => selectedSubscriptionIds[country])"
                    />
                    <small> Select All</small>
                  </label>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="country in Object.keys(subscriptions).sort((a, b) => countriesList[a].localeCompare(countriesList[b]))" :key="`country-${country}`">
              <td>{{ countriesList[country] }}</td>
              <td>
                <div>
                  <label>
                  <input
                    type="checkbox"
                    :checked="subscriptions[country].psu_types.includes('personal')"
                    @change="togglePsuType(country, 'personal', $event.target.checked)"
                  />
                  Personal
                  </label>
                </div>
                <div>
                  <label>
                  <input
                    type="checkbox"
                    :checked="subscriptions[country].psu_types.includes('business')"
                    @change="togglePsuType(country, 'business', $event.target.checked)"
                  />
                  Business
                  </label>
                </div>
              </td>
              <td>
                <div>
                  <label>
                  <input
                    type="checkbox"
                    :checked="subscriptions[country].services.includes('AIS')"
                    @change="toggleService(country, 'AIS', $event.target.checked)"
                  />
                  AIS
                  </label>
                </div>
                <div>
                  <label>
                  <input
                    type="checkbox"
                    :checked="subscriptions[country].services.includes('PIS')"
                    @change="toggleService(country, 'PIS', $event.target.checked)"
                  />
                  PIS
                  </label>
                </div>
              </td>
              <td>
                <div>
                  <label>
                  <input
                    type="checkbox"
                    :checked="selectedSubscriptionIds[country]"
                    @change="toggleUnsubscribe(country, subscriptionIds[country], $event.target.checked)"
                  />
                  Unsubscribe
                  </label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="d-flex mt-3">
          <button class="btn btn-primary me-3" @click="updateAndUnsubscribe">Update Subscriptions</button>
        </div>
        <div>
          <p></p>
          <p>You can subscribe to more countries by visiting <a href="/cp/aspsps">ASPSP status</a> page</p>
        </div>
      </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions, mapState} from 'vuex';
import metadata from '../mixins/metadata.js';
import SMSEnrollment from '../components/SMSEnrollment.vue';
import axios from 'axios';

export default {
  name: 'UserProfile',
  mixins: [metadata],
  data() {
    return {
      showSMSEnrollment: false,
      loadingASPSPs: false,
      countries: null,
      country: '',
      subscriptions: {},
      isSubscribed: {},
      subscriptionIds: {},
      selectedSubscriptionIds: {},
      brokerOrigin: localStorage.getItem('selected_broker') || ''
    };
  },
  watch: {
    brokerOrigin: {
      handler(brokerOrigin) {
        localStorage.setItem('selected_broker', brokerOrigin);
        (async () => {
          await this.getUserSubscriptions(brokerOrigin)
        })()
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters(['user', 'loading', 'idToken']),
    ...mapState({
      countriesList: (state) => state.countriesList,
      brokerOriginsNameMap: (state) => state.brokerOriginsNameMap
    }),
    selectedBroker() {
      // get selected broker from local storage
      return localStorage.getItem('selected_broker') || ''
    },
    brokerOrigins() {
      const userOriginsStr = this.idToken.claims.brokerOrigins
      if (!userOriginsStr) {
        return []
      }
      const defaultBrokerOrigin = ''
      const allowedOrigins = [
        {
          name: 'Default broker',
          value: defaultBrokerOrigin,
          selected: defaultBrokerOrigin === this.selectedBroker
        }
      ]
      const userOrigins = userOriginsStr.split(',')
      if (this.selectedBroker && !userOrigins.includes(this.selectedBroker)) {
        // If the user lost access to the selected broker, reset the selection
        localStorage.setItem('selected_broker', '')
      }
      userOrigins.forEach((origin) => {
        if (this.brokerOriginsNameMap[origin]) {
          allowedOrigins.push({
            name: this.brokerOriginsNameMap[origin],
            value: origin,
            selected: origin === this.selectedBroker
          })
        }
      })
      return allowedOrigins
    },
    isSMSFactorEnabled() {
      const { enrolledFactors } = this.user.multiFactor;
      return enrolledFactors && enrolledFactors.some(factor => factor.factorId === "phone");
    },
  },
  components: {
    SMSEnrollment
  },
  methods: {
    ...mapActions(['pushAlertMessage', 'getSubscriptions', 'unsubscribe']),
    toggleSMSEnrollment() {
      this.showSMSEnrollment = !this.showSMSEnrollment;
    },
    getPhoneNumber() {
      const { enrolledFactors } = this.user.multiFactor;
      const phoneFactor = enrolledFactors.find(factor => factor.factorId === 'phone');
      return phoneFactor ? phoneFactor.phoneNumber : '';
    },
    async getUserSubscriptions(broker) {
      const payload = {}
      if (broker) {
        payload.broker = broker
      }
      const subscriptionsResponse = await this.getSubscriptions(payload)
      const allSubscriptions = {}
      const subscriptions = subscriptionsResponse.data.subscriptions
      for (const subscription of subscriptions) {
        // Check if the country already exists in allSubscriptions
        if (!(subscription.country in allSubscriptions)) {
          allSubscriptions[subscription.country] = {}
        }
        allSubscriptions[subscription.country] = subscription
      }
      this.subscriptions = allSubscriptions
      console.log('subscriptions:', this.subscriptions)
      // Initialize isSubscribed and subscriptionIds for each country
      for (const country in this.countriesList) {
        // Find the subscription for the current country
        const subscription = subscriptions.find(
          (sub) => sub.country === country
        )
        // Set isSubscribed[country] to true if a subscription exists,otherwise false
        this.$set(this.isSubscribed, country, !!subscription)

        // Store the subscription ID if a subscription exists
        this.$set(this.subscriptionIds, country, subscription ? subscription.kid : null)
      }
    },
    togglePsuType(country, type, isChecked) {
      if (!this.subscriptions[country]) {
        return
      }
      const psuTypes = this.subscriptions[country].psu_types || []
      if (isChecked) {
        // Add the type if it's not already in the array
        if (!psuTypes.includes(type)) {
          psuTypes.push(type)
        }
        // Uncheck the "Unsubscribe" checkbox
        this.$delete(this.selectedSubscriptionIds, country)
      } else {
        // Remove the type if it's in the array
        const index = psuTypes.indexOf(type)
        if (index > -1) {
          psuTypes.splice(index, 1)
        }
      }
      this.$set(this.subscriptions[country], 'psu_types', [...psuTypes])
    },
    toggleService(country, service, isChecked) {
      if (!this.subscriptions[country]) {
        return
      }
      const services = this.subscriptions[country].services || []
      if (isChecked) {
        // Add the service if it's not already in the array
        if (!services.includes(service)) {
          services.push(service)
        }
        // Uncheck the "Unsubscribe" checkbox
        this.$delete(this.selectedSubscriptionIds, country)
      } else {
        // Remove the service if it's in the array
        const index = services.indexOf(service)
        if (index > -1) {
          services.splice(index, 1)
        }
      }
      this.$set(this.subscriptions[country], 'services', [...services])
    },
    toggleUnsubscribe(country, subscriptionId, isChecked) {
      if (isChecked) {
        // Add the subscription ID to selectedSubscriptionIds
        this.$set(this.selectedSubscriptionIds, country, subscriptionId)

        // Clear all PSU types and services for the country
        if (this.subscriptions[country]) {
          this.$set(this.subscriptions[country], 'psu_types', [])
          this.$set(this.subscriptions[country], 'services', [])
        }
      } else {
        // Remove the subscription ID from selectedSubscriptionIds
        this.$delete(this.selectedSubscriptionIds, country)
      }
    },
    async updateAndUnsubscribe() {
      try {
        // Handle unsubscriptions
        if (Object.keys(this.selectedSubscriptionIds).length > 0) {
          for (const [country, subscriptionId] of Object.entries(this.selectedSubscriptionIds)) {
            if (!subscriptionId) {
              continue // Skip if no subscription ID is provided
            }
            const payload = {
              subscriptionId: subscriptionId
            }
            const response = await this.unsubscribe(payload)
            this.$delete(this.subscriptions, country)
            console.log('Unsubscribe response:', response.data)
          }
        }
        // Update subscriptions
        const payload = {
          subscriptions: this.subscriptions
        }
        const response = await axios.patch('/api/subscriptions', payload, {
          headers: {
            Authorization: `Bearer ${this.idToken.token}`
          }
        })
        this.pushAlertMessage({ message: 'Subscriptions updated successfully!', type: 'info' })
        console.log('Update response:', response.data)
      } catch (error) {
        console.error('Error during update and unsubscribe:', error)
        this.pushAlertMessage({ message: 'Failed to update subscriptions or unsubscribe.', type: 'error' })
      }
    },
    toggleUnsubscribeAll(isChecked) {
      Object.keys(this.subscriptions).forEach((country) => {
        if (isChecked) {
          // Add all countries to selectedSubscriptionIds
          this.$set(this.selectedSubscriptionIds, country, this.subscriptionIds[country])

          // Clear all PSU types and services for the country
          if (this.subscriptions[country]) {
            this.$set(this.subscriptions[country], 'psu_types', [])
            this.$set(this.subscriptions[country], 'services', [])
          }
        } else {
          // Remove all countries from selectedSubscriptionIds
          this.$delete(this.selectedSubscriptionIds, country)
        }
      })
    }
  }
}
</script>
